import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import Icon1 from "@assets/icons/Icon";
import { ordresApi } from "@services/api/ordres";
import { useDataStore } from "@store/user_org";
import type { Ordre, OrdreStatus, OrdreTimelineEvent, OrdreDocument } from "types/ordre";

// ─── Constants ──────────────────────────────────────────────

const COLORIS_TO_PAINT: Record<string, string> = {
  noir: "black", blanc: "white", gris: "grey", bleu: "blue",
  rouge: "red", vert: "green", jaune: "yellow", marron: "brown",
  orange: "orange", autre: "silver",
};

function getVehicleImageUrl(marque?: string, modele?: string, coloris?: string): string | null {
  if (!marque || !modele) return null;
  const make = encodeURIComponent(marque.toLowerCase());
  const model = encodeURIComponent(modele.toLowerCase());
  const paint = COLORIS_TO_PAINT[coloris || ""] || "grey";
  return `https://cdn.imagin.studio/getimage?customer=img&make=${make}&modelFamily=${model}&modelYear=2023&zoomType=fullscreen&angle=0&paintDescription=${paint}`;
}

const STATUS_DISPLAY: Partial<Record<OrdreStatus, { label: string; bg: string; color: string }>> = {
  a_preparer: { label: "en prépa", bg: "#F4F4F4", color: "#A1A1A1" },
  commande: { label: "commandé", bg: "#ECFFFE", color: "#47DDD5" },
  pret: { label: "prêt", bg: "#E8EAFF", color: "#5C6BFF" },
  reception: { label: "réception", bg: "#F4F4F4", color: "#A1A1A1" },
  en_cours: { label: "en cours", bg: "#CACFFF", color: "#5C6BFF" },
  devis: { label: "devis", bg: "#FEF3C7", color: "#D97706" },
  accord: { label: "accord", bg: "#D1FAE5", color: "#059669" },
  fin_de_travaux: { label: "terminé", bg: "#D1FAE5", color: "#059669" },
  facturation: { label: "facturation", bg: "#E8EAFF", color: "#6366f1" },
  disponible: { label: "disponible", bg: "#F1E7F9", color: "#8258FF" },
  restitue: { label: "restitué", bg: "#D1FAE5", color: "#059669" },
};

// ─── Timeline (reused from OrdreDetailScreen) ───────────────

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;
const IMAGE_EXT_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function formatEventDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function MobileTimelineDocThumbnail({ docName, documents, ordreId }: { docName: string; documents: OrdreDocument[]; ordreId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    const doc = documents.find((d) => d.name === docName);
    if (!doc) return;
    let cancelled = false;
    ordresApi.getDocumentUrl(ordreId, doc.id).then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [docName, documents, ordreId]);

  return (
    <div className="flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ width: 64, height: 64, borderRadius: 8, background: "#F2F2F2" }}>
      {url ? (
        <img src={url} alt={docName} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={20} color="#D4D4D4" />
      )}
    </div>
  );
}

function MobileTimelineEvent({ event, isLast, documents, ordreId }: { event: OrdreTimelineEvent; isLast: boolean; documents?: OrdreDocument[]; ordreId?: string }) {
  const imageNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      {/* Date */}
      <div className="flex-shrink-0" style={{ width: 75, paddingTop: 2, textAlign: "right", paddingRight: 8 }}>
        <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "8px", color: "#A1A1A1" }}>
          {formatEventDate(event.date)}
        </span>
      </div>

      {/* Dot + line */}
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 16 }}>
        <div className="rounded-full mt-1 flex-shrink-0" style={{ width: 8, height: 8, background: "#A1A1A1" }} />
        {!isLast && <div style={{ width: 1, background: "#A1A1A1" }} className="flex-1 my-0.5" />}
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 ml-2 flex-1 pb-5" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
            <Avvvatars value={event.user_name} style="shape" size={28} />
          </div>
          <div className="flex flex-col">
            <span style={{ fontWeight: 500, fontSize: 12, lineHeight: "90%", color: "#000000" }}>{event.user_name}</span>
            <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A1A1A1" }}>{event.action}</span>
          </div>
        </div>

        {/* Image thumbnails from document_names */}
        {imageNames.length > 0 && documents && ordreId && (
          <div className="flex flex-row flex-wrap" style={{ gap: 4, paddingLeft: 34 }}>
            {imageNames.map((name, i) => (
              <MobileTimelineDocThumbnail key={i} docName={name} documents={documents} ordreId={ordreId} />
            ))}
          </div>
        )}

        {/* Non-image documents */}
        {fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5" style={{ paddingLeft: 34 }}>
            {fileNames.map((name, i) => (
              <div key={i} className="flex flex-row items-center gap-2">
                <Icon1 name="file" size={11} color="#A1A1A1" />
                <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A1A1A1" }}>{name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Photos from photo_urls */}
        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row flex-wrap" style={{ gap: 4, paddingLeft: 34 }}>
            {event.photo_urls.map((url, i) => (
              <div key={i} className="flex-shrink-0 overflow-hidden" style={{ width: 64, height: 64, borderRadius: 8, background: "#F2F2F2" }}>
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}

        {/* Status change */}
        {event.type === "status_change" && event.status_label && (
          <div style={{ paddingLeft: 34 }}>
            <div
              className="inline-flex flex-row items-center"
              style={{ padding: "2px 6px", gap: 6, background: "#ECFFFE", borderRadius: 6 }}
            >
              <span style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#47DDD5" }}>
                {event.status_label}
              </span>
              <Icon1 name="done" size={13} color="#47DDD5" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Documents tab (mobile) ──────────────────────────────────

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXT_RE.test(d.gcs_path || d.name);
}

function MobileDocThumbnail({ doc, ordreId }: { doc: OrdreDocument; ordreId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!doc.gcs_path) return;
    let cancelled = false;
    ordresApi.getDocumentUrl(ordreId, doc.id).then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id, ordreId]);

  return (
    <div className="flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ width: 80, height: 80, borderRadius: 8, background: "#F2F2F2" }}>
      {url ? (
        <img src={url} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={24} color="#D4D4D4" />
      )}
    </div>
  );
}

function MobileDocumentsTab({ documents, ordreId }: { documents: OrdreDocument[]; ordreId: string }) {
  const photos = documents.filter(isImageDoc);
  const files = documents.filter((d) => !isImageDoc(d));

  if (documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <span style={{ fontSize: 13, color: "#A8A8A8" }}>Aucun document</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {photos.length > 0 && (
        <div className="flex flex-col" style={{ gap: 6 }}>
          <span style={{ fontWeight: 500, fontSize: 11, color: "#A8A8A8" }}>Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap" style={{ gap: 6 }}>
            {photos.map((p) => (
              <MobileDocThumbnail key={p.id} doc={p} ordreId={ordreId} />
            ))}
          </div>
        </div>
      )}
      {files.length > 0 && (
        <div className="flex flex-col" style={{ gap: 4 }}>
          {photos.length > 0 && <span style={{ fontWeight: 500, fontSize: 11, color: "#A8A8A8" }}>Documents ({files.length})</span>}
          {files.map((d) => (
            <div key={d.id} className="flex flex-row items-center gap-2" style={{ padding: "6px 0" }}>
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: 6, background: "#F2F2F2" }}>
                <Icon1 name={d.type === "pdf" ? "file" : "image"} size={14} color="#A1A1A1" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="truncate" style={{ fontWeight: 500, fontSize: 12, color: "#1E1E1E" }}>{d.name}</span>
                {d.created_at && <span style={{ fontWeight: 400, fontSize: 10, color: "#A8A8A8" }}>{d.created_at}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────

export default function MobileOrdreDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const userData = useDataStore((s) => s.userData);

  const [ordre, setOrdre] = useState<Ordre | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"fil" | "documents">("fil");
  const [uploading, setUploading] = useState(false);
  const [uploadedPreviews, setUploadedPreviews] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    ordresApi.get(id).then((o) => setOrdre(o)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const handleUpload = async (files: FileList) => {
    if (!ordre || !id) return;
    setUploading(true);
    setUploadSuccess(false);

    // Show local previews immediately
    const previews: string[] = [];
    for (const file of Array.from(files)) {
      if (file.type.startsWith("image/")) {
        previews.push(URL.createObjectURL(file));
      }
    }
    setUploadedPreviews((prev) => [...prev, ...previews]);

    try {
      let updated: Ordre | null = null;
      for (const file of Array.from(files)) {
        const docType = file.type.startsWith("image/") ? "photo" : undefined;
        updated = await ordresApi.uploadDocument(id, file, undefined, docType);
      }
      if (updated) setOrdre(updated);
      setUploadSuccess(true);
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (e) {
      console.error("Upload failed:", e);
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <span style={{ fontFamily: "Poppins", fontSize: 13, color: "#A8A8A8" }}>Chargement...</span>
      </div>
    );
  }

  if (!ordre) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <span style={{ fontFamily: "Poppins", fontSize: 14, color: "#A8A8A8" }}>Ordre introuvable</span>
        <button onClick={() => navigate("/m")} style={{ fontFamily: "Poppins", fontSize: 14, color: "#5C6BFF", textDecoration: "underline" }}>
          Retour
        </button>
      </div>
    );
  }

  const status = STATUS_DISPLAY[ordre.status];
  const vehicle = ordre.vehicle;
  const imgUrl = vehicle ? getVehicleImageUrl(vehicle.marque, vehicle.modele, vehicle.coloris) : null;
  const timeline = ordre.timeline || [];
  const sorted = [...timeline].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="flex flex-col h-screen bg-white" style={{ fontFamily: "Poppins" }}>
      {/* Content */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "30px 20px 0" }}>
        {/* Order card — tappable to go back */}
        <button
          onClick={() => navigate("/m")}
          className="w-full flex flex-row items-stretch text-left"
          style={{
            padding: 4, gap: 10,
            background: "#FFFFFF",
            boxShadow: "0px 2px 6.3px rgba(0, 0, 0, 0.15)",
            borderRadius: 10,
          }}
        >
          {/* Vehicle thumbnail */}
          <div
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: 69, height: 64, background: "#F2F2F2", borderRadius: 10, overflow: "hidden" }}
          >
            {imgUrl ? (
              <img src={imgUrl} alt="" style={{ width: 59, objectFit: "contain" }} />
            ) : (
              <Icon1 name="car" size={24} color="#A1A1A1" />
            )}
          </div>

          {/* Info */}
          <div className="flex flex-col justify-center flex-1" style={{ padding: "4px 0", gap: 3, minWidth: 0 }}>
            <span className="truncate" style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#1E1E1E" }}>
              {ordre.name}
            </span>
            <div className="flex flex-col" style={{ gap: 2 }}>
              <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                {ordre.num}
              </span>
              <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                {ordre.immatriculation || vehicle?.plate || "—"}
              </span>
            </div>
          </div>

          {/* Status */}
          {status && (
            <div
              className="flex items-center justify-center flex-shrink-0 self-end"
              style={{ padding: "4px 10px", background: status.bg, borderRadius: 5 }}
            >
              <span style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: status.color }}>
                {status.label}
              </span>
            </div>
          )}
        </button>

        {/* Tab switcher */}
        <div className="flex flex-row" style={{ marginTop: 20, gap: 0, borderBottom: "1px solid #E5E5E5" }}>
          {([["fil", "Fil de l'ordre"], ["documents", "Documents"]] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              style={{
                flex: 1,
                padding: "8px 0",
                fontWeight: 500,
                fontSize: 13,
                color: activeTab === key ? "#1E1E1E" : "#A8A8A8",
                borderBottom: activeTab === key ? "2px solid #1E1E1E" : "2px solid transparent",
                background: "none",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ marginTop: 15, paddingBottom: 10 }}>
          {activeTab === "fil" && (
            sorted.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <span style={{ fontSize: 13, color: "#A8A8A8" }}>Aucun événement</span>
              </div>
            ) : (
              <div className="flex flex-col">
                {sorted.map((ev, i) => (
                  <MobileTimelineEvent key={ev.id} event={ev} isLast={i === sorted.length - 1} documents={ordre.documents} ordreId={ordre.id} />
                ))}
              </div>
            )
          )}
          {activeTab === "documents" && (
            <MobileDocumentsTab documents={ordre.documents || []} ordreId={ordre.id} />
          )}
        </div>
      </div>

      {/* Uploaded previews */}
      {uploadedPreviews.length > 0 && (
        <div className="flex flex-row flex-wrap" style={{ padding: "0 20px", gap: 4 }}>
          {uploadedPreviews.map((url, i) => (
            <img key={i} src={url} alt="" className="object-cover" style={{ width: 60, height: 60, borderRadius: 8, opacity: uploading ? 0.5 : 1 }} />
          ))}
        </div>
      )}

      {/* Success message */}
      {uploadSuccess && (
        <div className="flex items-center justify-center" style={{ padding: "0 20px" }}>
          <span style={{ fontWeight: 500, fontSize: 13, color: "#10b981" }}>
            Photos ajoutées avec succès
          </span>
        </div>
      )}

      {/* Add photos button */}
      <div style={{ padding: "10px 20px" }}>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center"
          style={{
            height: 50,
            background: uploading ? "#D4D4D4" : "#E4E4E4",
            borderRadius: 30,
          }}
        >
          <span style={{ fontWeight: 500, fontSize: 14, lineHeight: "21px", color: "#636363" }}>
            {uploading ? "Envoi en cours..." : "Ajouter des photos"}
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          capture="environment"
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleUpload(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {/* Back button area */}
      <div className="flex justify-center" style={{ paddingBottom: 8 }}>
        <button
          onClick={() => navigate("/m")}
          style={{ width: 104, height: 5, background: "#000000", borderRadius: 30 }}
        />
      </div>
    </div>
  );
}
